import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

/**
 * Caso 16 — Navigation cohesion across the
 * `Brand › Project › Event › Station` hierarchy.
 *
 * Items render as links unless `to` is omitted (the last item is typically
 * the current page, rendered as plain text). The component self-hides when
 * the list is empty so callers can pass a partial chain without guards.
 */
export function Breadcrumbs({
  items,
  className,
}: {
  items: BreadcrumbItem[];
  className?: string;
}) {
  if (items.length === 0) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        'flex flex-wrap items-center gap-1 text-xs text-[var(--color-fg-muted)]',
        className,
      )}
    >
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <span key={`${i}-${item.label}`} className="inline-flex items-center gap-1">
            {item.to && !last ? (
              <Link
                to={item.to}
                className="rounded px-1 hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-fg-primary)]"
              >
                {item.label}
              </Link>
            ) : (
              <span
                className={
                  last
                    ? 'font-semibold text-[var(--color-fg-primary)]'
                    : 'px-1'
                }
                aria-current={last ? 'page' : undefined}
              >
                {item.label}
              </span>
            )}
            {!last && <ChevronRight className="h-3 w-3 opacity-60" />}
          </span>
        );
      })}
    </nav>
  );
}
